import { useMemo, useState } from "react";
import { useLang } from "@/lib/i18n";

const COMPETITORS = [
  { id: "stripe", name: "Stripe", percent: 2.9, fixed: 0.30, color: "#635BFF" },
  { id: "worldline", name: "Worldline / SIX", percent: 2.5, fixed: 0.19, color: "#E50019" },
  { id: "postfinance", name: "PostFinance", percent: 2.5, fixed: 0.20, color: "#FFCC00" },
];
const SWISS = { percent: 1.4, fixed: 0.20 };

export default function SavingsCalculator() {
  const { lang, t } = useLang();
  const [volume, setVolume] = useState(250000);
  const [avgTicket, setAvgTicket] = useState(45);
  const [competitor, setCompetitor] = useState("stripe");
  const c = COMPETITORS.find((x) => x.id === competitor);

  const fmt = useMemo(
    () => (n) => new Intl.NumberFormat(lang === "fr" ? "fr-CH" : "de-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 0 }).format(Math.max(0, Math.round(n))),
    [lang],
  );
  const numFmt = useMemo(
    () => (n) => new Intl.NumberFormat(lang === "fr" ? "fr-CH" : "de-CH").format(n),
    [lang],
  );

  const { theirCost, ourCost, monthlySaving, yearlySaving, txns } = useMemo(() => {
    const txns = Math.max(1, Math.floor(volume / Math.max(1, avgTicket)));
    const theirCost = volume * (c.percent / 100) + txns * c.fixed;
    const ourCost = volume * (SWISS.percent / 100) + txns * SWISS.fixed;
    const monthlySaving = theirCost - ourCost;
    return { theirCost, ourCost, monthlySaving, yearlySaving: monthlySaving * 12, txns };
  }, [volume, avgTicket, c]);

  return (
    <section id="savings" data-testid="savings-section" className="py-24 lg:py-32 relative">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5">
            <span className="pill" style={{ borderColor: "rgba(220,38,38,0.35)", color: "#DC2626" }}>
              <i className="fa-solid fa-credit-card" /> {t("savings.pill")}
            </span>
            <h2 className="mt-5 font-display font-semibold text-4xl sm:text-5xl lg:text-[60px] leading-[1.0] tracking-tight max-w-[16ch]">
              {t("savings.titleLead")}
              <em className="italic font-light text-[#DC2626]">{t("savings.titleEm")}</em>
              {t("savings.titleTail")}
            </h2>
            <p className="mt-6 max-w-md text-[17px] leading-relaxed text-[hsl(var(--ink-soft))]">
              {t("savings.paragraphLead")}
              <strong>{t("savings.paragraphBold")}</strong>
              {t("savings.paragraphTail")}
            </p>

            <ul className="mt-8 space-y-3 text-sm">
              {t("savings.bullets").map((b) => (
                <li key={b} className="flex items-center gap-3">
                  <span className="w-5 h-5 rounded-full bg-[#DC2626]/12 text-[#DC2626] flex items-center justify-center text-[10px]">
                    <i className="fa-solid fa-check" />
                  </span>
                  <span className="text-[hsl(var(--ink-soft))]">{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7 soft-card p-7 lg:p-9 relative overflow-hidden">
            <div className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(220,38,38,0.12) 0%, rgba(220,38,38,0) 70%)" }} />

            <div className="relative grid sm:grid-cols-2 gap-6">
              <SliderField
                label={t("savings.monthlyVolume")}
                value={volume}
                onChange={setVolume}
                min={5000}
                max={5_000_000}
                step={5000}
                format={fmt}
                testId="slider-volume"
              />
              <SliderField
                label={t("savings.avgTicket")}
                value={avgTicket}
                onChange={setAvgTicket}
                min={5}
                max={500}
                step={1}
                format={fmt}
                testId="slider-ticket"
              />
            </div>

            <div className="relative mt-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
                {t("savings.compareAgainst")}
              </div>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {COMPETITORS.map((x) => {
                  const active = x.id === competitor;
                  return (
                    <button
                      key={x.id}
                      onClick={() => setCompetitor(x.id)}
                      data-testid={`competitor-${x.id}`}
                      className={`text-xs font-medium px-3.5 h-9 rounded-full border transition-colors flex items-center gap-2 ${
                        active ? "text-white border-transparent" : "border-[hsl(var(--line))] hover:border-[hsl(var(--ink))] text-[hsl(var(--ink))]"
                      }`}
                      style={active ? { background: x.color } : {}}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: active ? "white" : x.color }} />
                      {x.name}
                      <span className="font-mono text-[10px] opacity-80">
                        {x.percent}% + {fmt(x.fixed)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="relative mt-8 grid sm:grid-cols-3 gap-px bg-[hsl(var(--line))] rounded-2xl overflow-hidden border border-[hsl(var(--line))]">
              <ResultCell
                label={t("savings.theirFees")(c.name)}
                value={fmt(theirCost)}
                hint={t("savings.txnsHint")(c.percent, fmt(c.fixed), numFmt(txns))}
                tone="muted"
                testId="result-their"
              />
              <ResultCell
                label={t("savings.ourFees")}
                value={fmt(ourCost)}
                hint={t("savings.sameVolumeHint")(SWISS.percent, fmt(SWISS.fixed))}
                tone="ink"
                testId="result-ours"
              />
              <ResultCell
                label={t("savings.youSave")}
                value={fmt(monthlySaving)}
                hint={t("savings.everyYear")(fmt(yearlySaving))}
                tone="accent"
                testId="result-savings"
              />
            </div>

            <div className="relative mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] max-w-md leading-relaxed">
                {t("savings.disclaimer")}
              </div>
              <a
                href="#contact"
                data-testid="savings-cta"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#DC2626] text-white font-medium hover:bg-[hsl(var(--ink))] transition-colors whitespace-nowrap"
              >
                {t("savings.cta")}
                <i className="fa-solid fa-arrow-right text-xs" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SliderField({ label, value, onChange, min, max, step, format, testId }) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{label}</label>
        <div className="font-display text-2xl font-semibold tabular-nums">{format(value)}</div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testId}
        className="mt-3 w-full slider-red"
      />
      <div className="mt-1 flex justify-between text-[10px] font-mono text-[hsl(var(--muted-foreground))]">
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
    </div>
  );
}

function ResultCell({ label, value, hint, tone = "muted", testId }) {
  const tones = {
    muted: "bg-[hsl(var(--bg-elev))] text-[hsl(var(--ink))]",
    ink: "bg-[hsl(var(--ink))] text-[hsl(var(--bg))]",
    accent: "bg-[#DC2626] text-white",
  };
  const subtle = {
    muted: "text-[hsl(var(--muted-foreground))]",
    ink: "text-white/60",
    accent: "text-white/85",
  };
  return (
    <div className={`p-6 ${tones[tone]}`} data-testid={testId}>
      <div className={`font-mono text-[10px] uppercase tracking-[0.2em] ${subtle[tone]}`}>{label}</div>
      <div className="mt-3 stat-num text-[34px] sm:text-[40px] leading-none tabular-nums">{value}</div>
      <div className={`mt-2 text-[11px] ${subtle[tone]}`}>{hint}</div>
    </div>
  );
}
